// Page hero scaffold (title/intro area at top of pages).
import React, { useState, useEffect, useRef } from "react";

export default function PageHero({
  title,
  subtitle,
  showSlider = false,
  showImage = false,
  slides = [],
   isRTL = false,
}) {
  //State to track the current slide index
  const [currentSlide, setCurrentSlide] = useState(0);
  //Ref to store the interval ID for auto-sliding
  const intervalRef = useRef(null);

const nextDir = isRTL ? -1 : 1;
const prevDir = isRTL ? 1 : -1;

  //Function to start the auto-sliding mechanism, which updates the current slide every 4 seconds
  const startAuto = () => {
    if (!showSlider || slides.length === 0) return;
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);
  };

  //Setup the auto-sliding interval when the component mounts or updates
  useEffect(() => {
    startAuto();
    return () => clearInterval(intervalRef.current);
  }, [showSlider, slides.length]);

  //function to handle manual navigation (next/prev)
  const go = (dir) => {
    clearInterval(intervalRef.current); // Stop timer when user clicka manually
    setCurrentSlide((prev) => (prev + dir + slides.length) % slides.length);
    startAuto(); // Restart timer after manual navigation
  };

  //Determine the title and subtitle to display based on whether the slider is active and has slides
  const displayTitle =
    showSlider && slides.length > 0 ? slides[currentSlide].title : title;
  const displaySubtitle =
    showSlider && slides.length > 0
      ? slides[currentSlide].description
      : subtitle;

  return (
    <section className="hero">
      {/*Background Pattern*/}
      <svg
        className="hero-pattern"
        viewBox="0 0 680 400"
        preserveAspectRatio="none"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g opacity="0.13">
          {[
            /*Array of coordinates and styles for background shapes */
            [10, 10, 28, 9, -55, "#411428"],
            [60, 40, 38, 9, 15, "#FF4B32"],
            [110, 8, 18, 9, -80, "#411428"],
            [155, 55, 34, 9, 30, "#733635"],
            [210, 15, 26, 9, -40, "#FF4B32"],
            [270, 70, 40, 9, 10, "#411428"],
            [330, 5, 22, 9, -70, "#733635"],
            [395, 45, 36, 9, 45, "#FF4B32"],
            [455, 20, 28, 9, -20, "#411428"],
            [515, 80, 32, 9, 60, "#733635"],
            [575, 12, 24, 9, -50, "#FF4B32"],
            [630, 50, 38, 9, 25, "#411428"],
            [30, 90, 30, 9, -65, "#FF4B32"],
            [180, 95, 26, 9, 40, "#733635"],
            [380, 100, 20, 9, -30, "#FF4B32"],
            [500, 105, 34, 9, 15, "#411428"],
            [20, 200, 32, 9, -45, "#733635"],
            [90, 230, 28, 9, 20, "#FF4B32"],
            [200, 190, 36, 9, -60, "#411428"],
            [320, 250, 24, 9, 35, "#733635"],
            [440, 210, 30, 9, -25, "#FF4B32"],
            [560, 240, 38, 9, 50, "#411428"],
            [15, 320, 26, 9, -40, "#FF4B32"],
            [140, 350, 34, 9, 25, "#733635"],
            [280, 310, 22, 9, -70, "#411428"],
            [420, 360, 30, 9, 15, "#FF4B32"],
            [590, 330, 28, 9, -55, "#733635"],
          ].map(([x, y, w, h, r, fill], i) => (
            <rect
              key={i}
              x={x}
              y={y}
              width={w}
              height={h}
              rx="2"
              fill={fill}
              transform={`rotate(${r} ${x} ${y})`}
            />
          ))}
        </g>
      </svg>

      {/* Main Hero Text (Title & Description) */}
      <div className="hero-content">
        <h1>{displayTitle}</h1>
        <p>{displaySubtitle}</p>
      </div>

      {/* Conditional rendering of the hero image and slider controls based on props */}
      {showImage && (
        <div className="hero-image">
          <img src="/hero-photo.png" alt="logo" />
        </div>
      )}
      {showSlider && (
        <div className="hero-controls">
          <button onClick={() => go(nextDir)} aria-label="next">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
          <button onClick={() => go(prevDir)} aria-label="prev">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        </div>
      )}
    </section>
  );
}
